import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { 
  FileText, 
  Download, 
  ChevronDown, 
  ChevronRight, 
  Calendar,
  Users,
  DollarSign,
  Shield,
  Heart,
  Clock,
  Search
} from 'lucide-react';

const EmployeeResources = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSections, setExpandedSections] = useState(new Set(['hr', 'payroll']));

  const resourceData = [
    {
      id: 'hr',
      title: 'HR Policies & Guidelines',
      icon: Users,
      color: 'bg-blue-600',
      items: [
        {
          name: 'Employee Handbook 2024',
          type: 'PDF',
          size: '2.3 MB',
          lastUpdated: '2024-01-01',
          description: 'Comprehensive guide to company policies, procedures, and expectations'
        },
        {
          name: 'Code of Conduct',
          type: 'PDF',
          size: '1.1 MB',
          lastUpdated: '2023-12-15',
          description: 'Professional behavior guidelines and ethical standards'
        },
        {
          name: 'Anti-Harassment Policy',
          type: 'PDF',
          size: '0.8 MB',
          lastUpdated: '2023-11-20',
          description: 'Workplace harassment prevention and reporting procedures'
        },
        {
          name: 'Remote Work Policy',
          type: 'PDF',
          size: '1.5 MB',
          lastUpdated: '2023-10-15',
          description: 'Guidelines for remote work arrangements and expectations'
        }
      ]
    },
    {
      id: 'payroll',
      title: 'Payroll Information',
      icon: DollarSign,
      color: 'bg-green-600',
      items: [
        {
          name: 'Payroll Schedule 2024',
          type: 'PDF',
          size: '0.5 MB',
          lastUpdated: '2023-12-20',
          description: 'Complete payroll calendar with pay dates and deadlines'
        },
        {
          name: 'Direct Deposit Form',
          type: 'Form',
          size: '0.2 MB',
          lastUpdated: '2023-11-01',
          description: 'Form to set up or change direct deposit information'
        },
        {
          name: 'Tax Withholding (W-4)',
          type: 'Form',
          size: '0.3 MB',
          lastUpdated: '2023-12-01',
          description: 'Federal tax withholding form for payroll deductions'
        },
        {
          name: 'Expense Reimbursement Policy',
          type: 'PDF',
          size: '0.9 MB',
          lastUpdated: '2023-09-15',
          description: 'Guidelines for business expense reimbursement process'
        }
      ]
    },
    {
      id: 'benefits',
      title: 'Benefits & Insurance',
      icon: Heart,
      color: 'bg-red-600',
      items: [
        {
          name: 'Health Insurance Guide',
          type: 'PDF',
          size: '3.2 MB',
          lastUpdated: '2024-01-01',
          description: 'Complete guide to health insurance plans and coverage options'
        },
        {
          name: 'Dental & Vision Benefits',
          type: 'PDF',
          size: '1.8 MB',
          lastUpdated: '2024-01-01',
          description: 'Information about dental and vision insurance coverage'
        },
        {
          name: '401(k) Retirement Plan',
          type: 'PDF',
          size: '2.1 MB',
          lastUpdated: '2023-11-15',
          description: 'Retirement savings plan details and enrollment information'
        },
        {
          name: 'Life Insurance Policy',
          type: 'PDF',
          size: '1.3 MB',
          lastUpdated: '2023-10-01',
          description: 'Life insurance coverage details and beneficiary information'
        },
        {
          name: 'Employee Assistance Program',
          type: 'PDF',
          size: '0.7 MB',
          lastUpdated: '2023-08-20',
          description: 'Mental health and wellness support resources'
        }
      ]
    },
    {
      id: 'time-off',
      title: 'Time Off & Holidays',
      icon: Calendar,
      color: 'bg-purple-600',
      items: [
        {
          name: 'Vacation Policy',
          type: 'PDF',
          size: '1.0 MB',
          lastUpdated: '2023-12-01',
          description: 'Vacation accrual, usage, and approval process'
        },
        {
          name: 'Company Holiday Schedule 2024',
          type: 'PDF',
          size: '0.4 MB',
          lastUpdated: '2023-11-30',
          description: 'Official company holidays and office closure dates'
        },
        {
          name: 'Sick Leave Policy',
          type: 'PDF',
          size: '0.8 MB',
          lastUpdated: '2023-10-15',
          description: 'Sick leave accrual and usage guidelines'
        },
        {
          name: 'FMLA Information',
          type: 'PDF',
          size: '1.4 MB',
          lastUpdated: '2023-09-01',
          description: 'Family and Medical Leave Act eligibility and procedures'
        }
      ]
    },
    {
      id: 'safety',
      title: 'Safety & Compliance',
      icon: Shield,
      color: 'bg-orange-600',
      items: [
        {
          name: 'Emergency Procedures',
          type: 'PDF',
          size: '1.6 MB',
          lastUpdated: '2023-12-10',
          description: 'Emergency evacuation and safety procedures'
        },
        {
          name: 'Workplace Safety Guidelines',
          type: 'PDF',
          size: '2.0 MB',
          lastUpdated: '2023-11-05',
          description: 'General workplace safety rules and best practices'
        },
        {
          name: 'Data Security Policy',
          type: 'PDF',
          size: '1.2 MB',
          lastUpdated: '2023-10-20',
          description: 'Information security and data protection guidelines'
        },
        {
          name: 'Incident Reporting Form',
          type: 'Form',
          size: '0.3 MB',
          lastUpdated: '2023-08-15',
          description: 'Form for reporting workplace incidents and accidents'
        }
      ]
    },
    {
      id: 'training',
      title: 'Training & Development',
      icon: Clock,
      color: 'bg-indigo-600',
      items: [
        {
          name: 'New Employee Orientation',
          type: 'PDF',
          size: '2.8 MB',
          lastUpdated: '2023-12-05',
          description: 'Complete orientation checklist and training schedule'
        },
        {
          name: 'Professional Development Policy',
          type: 'PDF',
          size: '1.1 MB',
          lastUpdated: '2023-10-30',
          description: 'Guidelines for professional development and training opportunities'
        },
        {
          name: 'Skills Training Catalog',
          type: 'PDF',
          size: '3.5 MB',
          lastUpdated: '2023-09-25',
          description: 'Available training courses and professional development programs'
        },
        {
          name: 'Performance Review Process',
          type: 'PDF',
          size: '1.7 MB',
          lastUpdated: '2023-08-30',
          description: 'Annual performance review process and evaluation criteria'
        }
      ]
    }
  ];

  const toggleSection = (sectionId) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const getFileTypeColor = (type) => {
    switch (type) {
      case 'PDF': return 'bg-red-100 text-red-800 border-red-300';
      case 'Form': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'Doc': return 'bg-blue-100 text-blue-800 border-blue-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const filteredResources = resourceData.map(section => ({
    ...section,
    items: section.items.filter(item =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(section => section.items.length > 0);

  const totalDocuments = resourceData.reduce((sum, section) => sum + section.items.length, 0);
  const filteredDocuments = filteredResources.reduce((sum, section) => sum + section.items.length, 0);

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Employee Resources</h1>
          <p className="text-gray-600 mt-2">Access company policies, benefits, and important documents</p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700">
          <FileText className="w-4 h-4 mr-2" />
          Upload Document
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6 text-center">
            <FileText className="w-8 h-8 mx-auto mb-2 text-blue-600" />
            <div className="text-2xl font-bold text-gray-900">{totalDocuments}</div>
            <div className="text-sm text-gray-600">Total Documents</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 text-center">
            <Users className="w-8 h-8 mx-auto mb-2 text-green-600" />
            <div className="text-2xl font-bold text-gray-900">{resourceData.length}</div>
            <div className="text-sm text-gray-600">Resource Categories</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 text-center">
            <Calendar className="w-8 h-8 mx-auto mb-2 text-purple-600" />
            <div className="text-2xl font-bold text-gray-900">12</div>
            <div className="text-sm text-gray-600">Company Holidays</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 text-center">
            <Clock className="w-8 h-8 mx-auto mb-2 text-orange-600" />
            <div className="text-2xl font-bold text-gray-900">25</div>
            <div className="text-sm text-gray-600">Vacation Days</div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search documents and resources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          {searchTerm && (
            <p className="text-sm text-gray-600 mt-2">
              Showing {filteredDocuments} of {totalDocuments} documents
            </p>
          )}
        </CardContent>
      </Card>

      {/* Resource Sections */}
      <div className="space-y-4">
        {filteredResources.map((section) => (
          <Card key={section.id} className="overflow-hidden">
            <Collapsible>
              <CollapsibleTrigger asChild>
                <CardHeader 
                  className="cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleSection(section.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        {expandedSections.has(section.id) ? 
                          <ChevronDown className="w-5 h-5 text-gray-400" /> : 
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        }
                      </div>
                      <div className={`p-2 rounded-lg ${section.color}`}>
                        <section.icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{section.title}</CardTitle>
                        <p className="text-sm text-gray-600 mt-1">
                          {section.items.length} documents available
                        </p>
                      </div>
                    </div>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              
              <CollapsibleContent>
                <CardContent className="pt-0 border-t bg-gray-50">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4">
                    {section.items.map((item, index) => (
                      <Card key={index} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <FileText className="w-4 h-4 text-gray-400" />
                                <h4 className="font-medium text-gray-900">{item.name}</h4>
                                <Badge className={getFileTypeColor(item.type)}>
                                  {item.type}
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-600 mb-2">{item.description}</p>
                              <div className="flex items-center space-x-4 text-xs text-gray-500">
                                <span>Size: {item.size}</span>
                                <span>Updated: {new Date(item.lastUpdated).toLocaleDateString()}</span>
                              </div>
                            </div>
                            <Button variant="ghost" size="sm" className="ml-2">
                              <Download className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default EmployeeResources;